
// FUTURE: Rename this file to `appletDesign.latest.namespace.ts`, this is going to generate a lot of changes

import { z } from 'zod';
import { appletDesignVersionConfigSchema_v4, appletVariableCfgSchema } from './latest/appletDesign.zod';
import {
    appletEventDefSchema,
    appletEventRefSchema,
} from './latest/event.zod';
import type { sparkplugAppletNodeCfgDeployParamSchema, sparkplugAppletNodeCfgManualSchema } from './latest/sparkplug-node-cfg.zod';
import type { locationDeployParamSchema, locationSchema } from './latest/location.deployParam';
import {
    baseComparisonSchema,
    baseOperandSchema,
    varComparisonOperandSchema,
    type varConditionSchema,
} from './latest/conditional.zod';
import type { varMediaObject } from './latest/cell-types/media.zod';
import { baseAppletVariableValue, type generatedAppletVariableCfgSchema, type varOrConstSchema } from './latest/var.zod';
import type { assetDeployParamSchema, assetSchema } from './latest/asset.deployParam';

// Applet design config imports based off of the lastest version
export const appletDesignVersionConfigSchemaLatest = appletDesignVersionConfigSchema_v4;

// These are types used elsewhere in the codebase that could not be derived from the applet design config
// NOTE: we should attempt to find a way to eliminate these types and use the types from the applet design config
// The less schemas defined here the less schemas we have to maintain/ bring forward every time we create a new schema version
export const latestAppletVariableValueSchema = baseAppletVariableValue;
export const latestAppletVariableCfgSchema = appletVariableCfgSchema;

// This is a reference map that can be used to determine which style a tab should use in the event that two of the conditions are true for the same tab
// FUTURE: Get these values from a tab group controller node
export const displayWeightRefMap: {
    [key in 'success' | 'info' | 'warning' | 'error']: number;
} = {
    success: 1,
    info: 2,
    warning: 3,
    error: 4,
};

export namespace Latest {

    export interface AppletDesignVersionConfig extends z.infer<typeof appletDesignVersionConfigSchemaLatest> {}

    export namespace Screen {

        export type Config = AppletDesignVersionConfig['screens'][number];

        export namespace Designer {

            export type GridLayoutAspectRatios = Config['gridLayoutAspectRatios'];

            export type GridLayoutAspectRatio = keyof GridLayoutAspectRatios;
            export type GridLayout = GridLayoutAspectRatios[GridLayoutAspectRatio];
            export type RootGridLayoutCell = GridLayout['gridConfig'];

            export type LinkedGridCell = GridLayout['linkedGridCells'][number];
            export type LinkedGridCellCoordinate = LinkedGridCell['coordinates'][number];
            export type ScreenRow = (LinkedGridCell & { type: 'subGrid'})['rows'][number];
            export type ScreenColumn = (LinkedGridCell & { type: 'subGrid'})['columns'][number];
            export type Coordinate = (LinkedGridCell & { type: 'subGrid'})['coordinates'];

            export type InfoBoxCell = LinkedGridCell & { type: 'infoBox' };
            export type SubGridCell = LinkedGridCell & { type: 'subGrid' };
            export type TabGroupCell = LinkedGridCell & { type: 'tabGroup' };
            export type RegularGridCell = LinkedGridCell & { type: 'regular' };
            export type TabInstanceCell = LinkedGridCell & { type: 'tabInstance' };

        }

        export namespace Cells {

            export type AppletCell = Config['cells'][number];

            export type AppletCellType = AppletCell['type'];

            export namespace Btn {
                export type Cell = AppletCell & { type: 'btn' };
                export type VariableBehaviorType = Exclude<Cell['attrs']['varBehavior'], undefined>['type']
            }
            export type TextCell = AppletCell & { type: 'text' };
            export type TimerCell = AppletCell & { type: 'timer' };
            export namespace Media {
                export type Cell = AppletCell & { type: 'media' };
                export type AppletMediaItem = Cell['attrs']['items'][number]['media'];
                export type VarMediaObject = z.infer<typeof varMediaObject>;
            }
            export type InputCell = AppletCell & { type: 'input' };
            export type DialogCell = AppletCell & { type: 'dialog' };
            export type DateTimeCell = AppletCell & { type: 'datetime' };
            export namespace AttrLookup {
                export type Cell = AppletCell & { type: 'attrLookup' };
                export type Condition = Cell['attrs']['conds'][number];
                export type PropMatchNum = Condition & { type: 'propMatchNum' };
            }
            export type MacroTargetCell = AppletCell & { type: 'macroTarget' };
            export namespace MaterialLookup {
                export type Cell = AppletCell & { type: 'materialLookup' };
                export type FilterComparison = Cell['attrs']['filterConditions'][number]['comparisons'][number];
            }
            export type CircularProgressCell = AppletCell & { type: 'circularProgress' };

            export type GaugeCell = AppletCell & { type: 'gauge' };

            export type WorkOrderModalCell = AppletCell & { type: 'workOrderModal' };

            export type WorkInstructionCell = AppletCell & { type: 'workInstruction' };

            export type MarkdownCell = AppletCell & { type: 'markdown' };

            export type ProcessListCell = AppletCell & { type: 'processList' };

            export type WorkInstructionProcessListCell = AppletCell & { type: 'workInstructionProcessList' };

            export namespace DisplayProcessCell {
                export type Cell = AppletCell & { type: 'displayProcess' };
                export type SimpleDisplayProcessCell = Cell & { attrs: Cell['attrs'] & { processData: { kind: 'simple' }}};
            }

            export type SelectCell = AppletCell & { type: 'select' };
        }
    }

    export namespace Macros {
        export type AppletMacro = AppletDesignVersionConfig['macros'][number];

        export type AppletMacroType = AppletMacro['type'];

        export type MaterialLookup = AppletMacro & { type: 'materialLookup' };
        export namespace Process {
            export type Macro = AppletMacro & { type: 'process' };
            export type CollectTriggerCfg = Macro['attrs']['collectTrigger'];
            export type DataCollection = Macro['attrs']['dataCollection'][number];
            export type LocalInputDataCollect = DataCollection & { source: 'localInput'};
            export type SparkplugNodeCfgType = (DataCollection & { source: 'sparkplug'})['varpCfg']['nodeCfg'];
        }
    }

    export namespace AppletEvent {
        export interface Ref extends z.infer<typeof appletEventRefSchema> {}
        export interface Def extends z.infer<typeof appletEventDefSchema> {}
    }

    export namespace AppletVariables {
        export type AppletVariables = AppletDesignVersionConfig['appletVariables'];
        export type AppletVariable = AppletVariables[string]
        export type AppletVariableType = AppletVariable['type']
        export type AppletVariableValue = AppletVariable['defaultValue'];
        export type LocalAppletVariable = AppletVariable & { kind: 'local' };
        export type DeploymentParamAppletVar = AppletVariable & { kind: 'deployParam' };
        export type GeneratedAppletVariable = z.infer<typeof generatedAppletVariableCfgSchema>
        export type VarOrConst<T extends AppletVariableValue = AppletVariableValue> = z.infer<ReturnType<typeof varOrConstSchema<T>>>;
    }

    // Conditional types
    export type BaseOperand = z.infer<typeof baseOperandSchema>;
    export type BaseComparison = z.infer<typeof baseComparisonSchema>;
    export type VarComparisonOperand = z.infer<typeof varComparisonOperandSchema>;
    export namespace AppletCondition {
        export type Condition = z.infer<typeof varConditionSchema>;
        export type EventCondition = Condition & { evalTrigger: 'event' };
        export type VariableChangeCondition = Condition & { evalTrigger: 'varChange' };
        export type Comparison = VariableChangeCondition['comparisons'][number];
        export type EqComparison = (Comparison & { type: 'eq' });
        export type TypeComparison = (Comparison & { type: 'type' });
        export type VarOperand = Comparison['operands'][number];
    }

    // Sparkplug types
    export type SparkplugAppletNodeCfgManual = z.infer<typeof sparkplugAppletNodeCfgManualSchema>;
    export type SparkplugAppletNodeCfgDeployParam = z.infer<typeof sparkplugAppletNodeCfgDeployParamSchema>;

    // Script types
    export type RuntimeScriptCfg = AppletDesignVersionConfig['scripts'][number];

    export type AllowedModifiers<C extends string> = {
        attr: C;
        displayName: string;
        allowedVarTypes?: string[];
    }[];

    export type LocationDeployParam = z.infer<typeof locationDeployParamSchema>;
    export type LocationCfg = z.infer<typeof locationSchema>;
    export type AssetDeployParam = z.infer<typeof assetDeployParamSchema>;
    export type AssetCfg = z.infer<typeof assetSchema>;
}
